import { useRef, useState, type ReactNode, type RefObject } from "react";
import { Reply } from "lucide-react";
import type { WSChatMessage } from "../../types";
import {
  formatChatTime,
  getChatGroupPosition,
  getChatReplyLabel,
  shouldGroupChatMessages,
} from "./chatMessageUtils";

type ChatMessageListProps = {
  messages: WSChatMessage[];
  viewerUserId: string;
  partnerName: string;
  readOnly?: boolean;
  listRef?: RefObject<HTMLDivElement | null>;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  topSlot?: ReactNode;
  bottomSlot?: ReactNode;
  emptyState?: ReactNode;
  highlightedMessageId?: string | null;
  onReply?: (message: WSChatMessage) => void;
};

export function ChatMessageList({
  messages,
  viewerUserId,
  partnerName,
  readOnly = false,
  listRef,
  onScroll,
  topSlot,
  bottomSlot,
  emptyState,
  highlightedMessageId: highlightedMessageIdProp = null,
  onReply,
}: ChatMessageListProps) {
  const [activeSwipe, setActiveSwipe] = useState<{ id: string; offset: number } | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(highlightedMessageIdProp);
  const swipeStartRef = useRef<{ id: string; x: number; mine: boolean } | null>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const highlightTimerRef = useRef<number | undefined>(undefined);

  const startSwipe = (message: WSChatMessage, mine: boolean, event: React.PointerEvent<HTMLDivElement>) => {
    if (readOnly) return;
    swipeStartRef.current = { id: message.id, x: event.clientX, mine };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveSwipe = (event: React.PointerEvent<HTMLDivElement>) => {
    if (readOnly) return;
    const start = swipeStartRef.current;
    if (!start) return;
    const rawOffset = event.clientX - start.x;
    const directionalOffset = start.mine ? Math.min(0, rawOffset) : Math.max(0, rawOffset);
    const offset = Math.max(-58, Math.min(58, directionalOffset));
    setActiveSwipe({ id: start.id, offset });
  };

  const endSwipe = (message: WSChatMessage) => {
    if (readOnly) return;
    const offset = activeSwipe?.id === message.id ? activeSwipe.offset : 0;
    if (Math.abs(offset) >= 42) onReply?.(message);
    swipeStartRef.current = null;
    setActiveSwipe(null);
  };

  const jumpToReplyTarget = (message: WSChatMessage) => {
    if (!message.replyToId) return;
    const target = messageRefs.current.get(message.replyToId);
    if (!target) return;
    window.clearTimeout(highlightTimerRef.current);
    setHighlightedMessageId(message.replyToId);
    target.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    highlightTimerRef.current = window.setTimeout(() => {
      setHighlightedMessageId(null);
    }, 1900);
  };

  const activeHighlightId = highlightedMessageIdProp ?? highlightedMessageId;

  return (
    <div className="chat-list" ref={listRef} onScroll={onScroll}>
      {topSlot}
      {!messages.length && emptyState}
      {messages.map((message, index) => {
        const mine = message.senderId === viewerUserId;
        const groupedWithPrevious = shouldGroupChatMessages(messages[index - 1], message);
        const groupedWithNext = shouldGroupChatMessages(message, messages[index + 1]);
        const groupPosition = getChatGroupPosition(groupedWithPrevious, groupedWithNext);
        const offset = activeSwipe?.id === message.id ? activeSwipe.offset : 0;
        const canJumpToReply = Boolean(message.replyToId && messages.some((item) => item.id === message.replyToId));

        return (
          <div
            ref={(node) => {
              if (node) messageRefs.current.set(message.id, node);
              else messageRefs.current.delete(message.id);
            }}
            className={[
              "message-row",
              mine ? "mine" : "",
              groupedWithPrevious ? "grouped" : "",
              message.replyToContent ? "has-reply" : "",
              activeHighlightId === message.id ? "jump-highlight" : "",
            ].filter(Boolean).join(" ")}
            key={message.id}
          >
            {message.replyToContent && (
              <div className={`reply-context ${mine ? "mine" : ""}`}>
                <button
                  className="reply-context-card"
                  type="button"
                  onClick={() => jumpToReplyTarget(message)}
                  disabled={!canJumpToReply}
                  aria-label={`Jump to replied message: ${message.replyToContent}`}
                >
                  <span className="reply-context-label">
                    <Reply size={12} /> {getChatReplyLabel(message, mine, viewerUserId, partnerName)}
                  </span>
                  <p>{message.replyToContent}</p>
                </button>
              </div>
            )}
            <div className="message-interaction">
              <div
                className={`message ${mine ? "mine" : ""} ${groupPosition}`}
                onPointerDown={readOnly ? undefined : (event) => startSwipe(message, mine, event)}
                onPointerMove={readOnly ? undefined : moveSwipe}
                onPointerUp={readOnly ? undefined : () => endSwipe(message)}
                onPointerCancel={readOnly ? undefined : () => {
                  swipeStartRef.current = null;
                  setActiveSwipe(null);
                }}
                style={offset ? { transform: `translateX(${offset}px)` } : undefined}
              >
                <p>{message.content}</p>
                {!groupedWithNext && <time>{formatChatTime(message.createdAt)}</time>}
              </div>
              {!readOnly && onReply && (
                <button
                  className="reply-action"
                  type="button"
                  onClick={() => onReply(message)}
                  aria-label={`Reply to ${mine ? "your message" : `${partnerName}'s message`}`}
                  title="Reply"
                >
                  <Reply size={15} />
                </button>
              )}
            </div>
          </div>
        );
      })}
      {bottomSlot}
    </div>
  );
}
